import { computed, onBeforeUnmount, ref, watch, type Ref, type ComputedRef } from 'vue'
import { timeFormatter } from '@/helpers/timeFormatter'
import { isMilliseconds } from '@/util/timeUtils'
import useAnimationFrame from '@/composables/base/useAnimationFrame'

export interface UseCountdownOptions {
    startTimestamp: Ref<number> | ComputedRef<number>
    duration: Ref<number> | ComputedRef<number>
    isActive: Ref<boolean> | ComputedRef<boolean>
}

function getRemainingSeconds (startEpoch: number, durationSeconds: number): number {
    if (!startEpoch || !durationSeconds) return -1

    const ms = isMilliseconds(startEpoch) ? startEpoch : startEpoch * 1000
    const elapsed = (Date.now() - ms) / 1000
    const remaining = durationSeconds - elapsed

    return remaining > 0 ? Math.ceil(remaining) : -1
}

export function useCountdown (options: UseCountdownOptions) {
    const { startTimestamp, duration, isActive } = options
    const remainingSeconds = ref(-1)

    const { pause, resume } = useAnimationFrame(
        () => {
            if (!isActive.value) {
                remainingSeconds.value = -1
                return
            }

            remainingSeconds.value = getRemainingSeconds(
                startTimestamp.value,
                duration.value
            )
        },
        {
            fpsLimit: 2,
            immediate: false
        }
    )

    const shouldShow = computed(() => {
        return remainingSeconds.value > 0
    })

    const displayTime = computed(() => {
        if (remainingSeconds.value <= 0) return ''
        return timeFormatter(remainingSeconds.value)
    })

    watch(isActive, (active) => {
        if (active) {
            resume()
        } else {
            remainingSeconds.value = -1
            pause()
        }
    }, { immediate: true })

    onBeforeUnmount(() => {
        pause()
    })

    return {
        shouldShow,
        displayTime,
        remainingSeconds
    }
}
